import { router, Head } from '@inertiajs/react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TruckIcon, CalendarIcon, MessageSquareIcon, BarChart3Icon, UsersIcon, CheckCircle2Icon } from 'lucide-react'

export default function Landing() {
  const [signInOpen, setSignInOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSignInClick = () => {
    // Check if user is already logged in
    const authToken = localStorage.getItem('auth_token')
    if (authToken) {
      // User is already logged in, redirect to dashboard
      router.visit('/dashboard')
      return
    }
    // User not logged in, show sign in modal
    setSignInOpen(true)
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const response = await fetch('/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (response.ok && data.jwt_token) {
        // Store tokens in localStorage
        localStorage.setItem('auth_token', data.jwt_token)
        localStorage.setItem('refresh_token', data.refresh_token)

        // Redirect to dashboard
        window.location.href = '/dashboard'
      } else {
        alert(data.error || 'Sign in failed. Please check your credentials.')
        setIsSubmitting(false)
      }
    } catch (error) {
      console.error('Sign in error:', error)
      alert('An error occurred. Please try again.')
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Head title="AI CRM by Cayu - AI-Powered CRM for Trucking Companies & Repair Shops">
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "AI CRM by Cayu",
            "applicationCategory": "BusinessApplication",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "description": "AI-powered CRM and intelligent agents for trucking companies and repair shops. Manage all marketing and sales channels, maximize conversions, boost profits, and enhance customer engagement with automated AI workflows.",
            "operatingSystem": "Web Browser",
            "softwareVersion": "1.0",
            "author": {
              "@type": "Organization",
              "name": "Cayu",
              "url": "https://cayu.ai"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5.0",
              "ratingCount": "1"
            },
            "featureList": [
              "AI-Powered Lead Management",
              "Multi-Channel Marketing Automation",
              "Intelligent Sales Pipeline",
              "Real-time Analytics & Reporting",
              "Driver Recruiting & Dispatch",
              "Customer Engagement Automation",
              "Instagram & Messenger Integration",
              "WhatsApp Business Integration",
              "AI Conversation Analysis"
            ],
            "audience": {
              "@type": "Audience",
              "audienceType": "Trucking Companies, Truck Repair Shops, Fleet Management"
            }
          })}
        </script>
      </Head>
      <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="container mx-auto flex h-20 items-center justify-between px-6 md:px-12">
          <div className="flex items-center gap-3">
            <div className="flex size-11 items-center justify-center rounded-full bg-gradient-to-br from-[#5589ed] to-[#4a7dd9] shadow-md">
              <svg className="size-6" viewBox="0 0 24 24" fill="white">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-2xl font-semibold text-[#151515]">AI CRM</span>
              <span className="text-xs text-[#666666] -mt-1">by Cayu</span>
            </div>
          </div>
          <Button
            onClick={handleSignInClick}
            className="rounded-lg bg-[#5589ed] hover:bg-[#4478dc] text-white px-6 h-10 font-medium transition-colors"
            data-testid="sign-in-button"
          >
            Sign In
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="relative">
        {/* Hero Background Image */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1565793298595-6a879b1d9492?q=80&w=2071&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(4px)',
            opacity: 0.15
          }}
        />

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-6 md:px-12 pt-32 pb-24">
          <div className="flex flex-col items-center text-center max-w-6xl mx-auto">
            {/* Badge */}
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#5589ed]/20 bg-white/90 backdrop-blur-sm px-4 py-2 shadow-sm">
              <CheckCircle2Icon className="size-4 text-[#5589ed]" />
              <span className="text-sm font-medium text-[#333333]">AI-Powered CRM for Trucking & Repair</span>
            </div>

            {/* Main Headline */}
            <h1 className="mb-6 text-6xl md:text-7xl lg:text-8xl font-bold leading-[1.1] text-[#151515] max-w-5xl">
              Maximize sales with
              {' '}<span className="text-[#5589ed]">AI automation</span>
            </h1>

            {/* Subheadline */}
            <p className="mb-10 max-w-2xl text-xl md:text-2xl text-[#666666] leading-relaxed">
              Manage all marketing channels, automate customer engagement, and boost conversions with intelligent AI agents built for trucking companies and repair shops
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
              <Button
                onClick={() => window.open('https://cayu.ai', '_blank')}
                className="rounded-lg bg-[#5589ed] hover:bg-[#4478dc] text-white px-8 h-12 text-lg font-semibold transition-colors shadow-lg hover:shadow-xl"
                data-testid="get-started-button"
              >
                Get Started
              </Button>
              <Button
                onClick={handleSignInClick}
                variant="outline"
                className="rounded-lg border-2 border-[#5589ed] text-[#5589ed] hover:bg-[#5589ed]/5 px-8 h-12 text-lg font-semibold transition-colors"
                data-testid="sign-in-hero-button"
              >
                Sign In
              </Button>
            </div>

            {/* Pricing */}
            <div className="mt-8 inline-flex items-center gap-2 text-[#666666]">
              <span className="text-lg">Starting at</span>
              <span className="text-3xl font-bold text-[#151515]">$1,000</span>
              <span className="text-lg">/month</span>
            </div>

          </div>
        </div>

        {/* Features Section */}
        <div className="relative z-10 mt-32 max-w-7xl mx-auto container px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#151515] mb-4">
              AI-powered tools for modern trucking
            </h2>
            <p className="text-xl text-[#666666] max-w-2xl mx-auto">
              Everything you need to manage leads, automate sales, and grow your trucking business
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: MessageSquareIcon,
                title: 'Multi-Channel Messaging',
                description: 'Manage Instagram, Facebook Messenger, WhatsApp, and YouTube comments from one platform. Never miss a lead from any channel.',
              },
              {
                icon: TruckIcon,
                title: 'AI Lead Analysis',
                description: 'Intelligent AI agents automatically analyze conversations, extract lead details, and identify sales opportunities in real-time.',
              },
              {
                icon: BarChart3Icon,
                title: 'Smart Sales Pipeline',
                description: 'Track every lead from first contact to closed deal. Automated status updates and conversion tracking maximize your revenue.',
              },
              {
                icon: CalendarIcon,
                title: 'Automated Scheduling',
                description: 'Google Calendar integration with two-way sync. Schedule appointments, send invites, and manage your entire calendar seamlessly.',
              },
              {
                icon: UsersIcon,
                title: 'Driver Recruiting',
                description: 'Dedicated pipeline for driver recruitment. Manage applicants, track hiring stages, and build your fleet faster.',
              },
              {
                icon: CheckCircle2Icon,
                title: 'Performance Analytics',
                description: 'Real-time dashboards show conversion rates, revenue metrics, and pipeline health. Make data-driven decisions instantly.',
              },
            ].map((feature) => (
              <div
                key={feature.title}
                className="rounded-2xl bg-white border border-gray-200 p-8 hover:border-[#5589ed] hover:shadow-lg transition-all duration-200"
              >
                <div className="inline-flex size-12 items-center justify-center rounded-xl bg-[#5589ed]/10 mb-5">
                  <feature.icon className="size-6 text-[#5589ed]" />
                </div>
                <h3 className="text-xl font-semibold text-[#151515] mb-3">{feature.title}</h3>
                <p className="text-[#666666] leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-12 mt-32 bg-gray-50">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-full bg-gradient-to-br from-[#5589ed] to-[#4a7dd9] shadow-md">
                <svg className="size-5" viewBox="0 0 24 24" fill="white">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-semibold text-[#151515]">AI CRM</span>
                <span className="text-xs text-[#666666] -mt-0.5">by Cayu</span>
              </div>
            </div>
            <p className="text-sm text-[#666666]">
              © 2025 AI CRM by{' '}
              <a
                href="https://cayu.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#5589ed] hover:underline font-medium"
              >
                Cayu
              </a>
              . All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Sign In Dialog */}
      <Dialog open={signInOpen} onOpenChange={setSignInOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">Welcome back</DialogTitle>
            <DialogDescription className="text-base">
              Sign in to your AI CRM account
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSignIn} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11"
                data-testid="password-input"
              />
            </div>
            <div className="flex items-center justify-between text-sm">
              <a
                href="/password/forgot"
                className="text-[#5589ed] hover:underline font-medium"
              >
                Forgot password?
              </a>
            </div>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-lg bg-[#5589ed] hover:bg-[#4478dc] text-white h-11 font-medium"
              data-testid="submit-sign-in-button"
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      </div>
    </>
  )
}
